import React, { useEffect, useState } from "react";
import { Row, Col, CardBody, Card, Alert, Container, Input, Label, Form, FormFeedback, Button } from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import { Link, useNavigate } from "react-router-dom";
import { registerUser, apiError } from "../../store/actions";
import { getCompanyName } from "constants/constAPI";
import ShreeRamImage from "../../components/Common/ShreeRamImage";

const Register = () => {
  document.title = "Register New Firm | 7-Day Free Trial";

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showPass, setShowPass] = useState(false);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      firmName: "",
      ownerName: "",
      mobileNo: "",
      email: "",
      city: "",
      state: "",
      gstNo: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      firmName: Yup.string()
        .min(3, "Firm name at least 3 characters hona chahiye")
        .required("Firm Name darj karein"),
      ownerName: Yup.string()
        .min(2, "Owner name at least 2 characters hona chahiye")
        .required("Owner/Contact Person name darj karein"),
      mobileNo: Yup.string()
        .matches(/^[6-9]\d{9}$/, "10-digit valid Indian mobile number darj karein")
        .required("Mobile number darj karein (Login ke liye use hoga)"),
      email: Yup.string()
        .email("Valid email format darj karein"),
      city: Yup.string().required("City darj karein"),
      state: Yup.string().required("State darj karein"),
      password: Yup.string()
        .min(4, "Password at least 4 characters ka hona chahiye")
        .required("Password darj karein"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password"), null], "Password match nahi ho raha hai")
        .required("Confirm password darj karein"),
    }),
    onSubmit: (values) => {
      dispatch(registerUser({
        firmName: values.firmName.trim(),
        ownerName: values.ownerName.trim(),
        mobileNo: values.mobileNo.trim(),
        email: values.email.trim(),
        city: values.city.trim(),
        state: values.state.trim(),
        gstNo: values.gstNo.trim(),
        password: values.password,
        username: values.mobileNo.trim(),
      }));
    },
  });

  const AccountProperties = createSelector(
    (state) => state.Account,
    (account) => ({
      user: account.user,
      registrationError: account.registrationError,
      success: account.success,
    })
  );

  const { user, registrationError, success } = useSelector(AccountProperties);

  useEffect(() => {
    dispatch(apiError(""));
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => navigate("/login"), 3000);
      return () => clearTimeout(timer);
    }
  }, [success, navigate]);

  return (
    <React.Fragment>
      <div
        className="account-pages py-5"
        style={{
          background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Container>
          <Row className="justify-content-center">
            <Col lg={10} xl={9}>
              <Card
                className="overflow-hidden border-0 shadow-lg"
                style={{ borderRadius: "20px" }}
              >
                <div
                  className="p-4 text-white text-center"
                  style={{
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  }}
                >
                  <div className="d-flex justify-content-center align-items-center gap-3 mb-2">
                    <div
                      className="bg-white rounded-circle p-1 d-inline-flex align-items-center justify-content-center"
                      style={{ width: "60px", height: "60px" }}
                    >
                      <ShreeRamImage
                        alt="Logo"
                        portraitStyle={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: "50%" }}
                        landscapeStyle={{ width: "100%", height: "100%", objectFit: "contain" }}
                      />
                    </div>
                    <div className="text-start">
                      <h3 className="text-white fw-bold mb-0">Register Your Firm</h3>
                      <p className="text-white-50 mb-0 font-size-14">
                        Multi-Firm Agricultural Brokerage & Ledger Management
                      </p>
                    </div>
                  </div>

                  {/* 7-Day Free Trial Banner */}
                  <div
                    className="mt-3 p-2 rounded-3 d-inline-flex align-items-center gap-2 px-4"
                    style={{
                      background: "rgba(255, 255, 255, 0.2)",
                      backdropFilter: "blur(5px)",
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                    }}
                  >
                    <i className="fas fa-gift text-warning font-size-18"></i>
                    <span className="fw-semibold text-white">
                      Instant 7-Day Full Feature Free Trial Included! No Credit Card Required.
                    </span>
                  </div>
                </div>

                <CardBody className="p-4 p-md-5">
                  {success && (
                    <Alert color="success" className="d-flex align-items-center gap-2 mb-4">
                      <i className="fas fa-check-circle font-size-20"></i>
                      <div>
                        <strong>Badhaai Ho!</strong> Aapki firm safaltapoorvak register ho gayi hai aur 7-day free trial active ho gaya hai. Ab aap login kar sakte hain...
                      </div>
                    </Alert>
                  )}

                  {registrationError && (
                    <Alert color="danger" className="d-flex align-items-center gap-2 mb-4">
                      <i className="fas fa-exclamation-triangle font-size-20"></i>
                      <div>
                        <strong>Registration Error:</strong> {registrationError}
                      </div>
                    </Alert>
                  )}

                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      validation.handleSubmit();
                    }}
                  >
                    <h5 className="text-primary fw-semibold mb-3 border-bottom pb-2">
                      <i className="fas fa-building me-2"></i>Firm & Owner Details
                    </h5>

                    <Row>
                      <Col md={6} className="mb-3">
                        <Label className="form-label fw-semibold">
                          Firm Name <span className="text-danger">*</span>
                        </Label>
                        <Input
                          name="firmName"
                          placeholder="e.g. Radhey Trading Co."
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.firmName}
                          invalid={validation.touched.firmName && !!validation.errors.firmName}
                          className="form-control-lg fs-6"
                        />
                        {validation.touched.firmName && validation.errors.firmName && (
                          <FormFeedback>{validation.errors.firmName}</FormFeedback>
                        )}
                      </Col>

                      <Col md={6} className="mb-3">
                        <Label className="form-label fw-semibold">
                          Owner / Contact Person <span className="text-danger">*</span>
                        </Label>
                        <Input
                          name="ownerName"
                          placeholder="e.g. Ramesh Kumar"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.ownerName}
                          invalid={validation.touched.ownerName && !!validation.errors.ownerName}
                          className="form-control-lg fs-6"
                        />
                        {validation.touched.ownerName && validation.errors.ownerName && (
                          <FormFeedback>{validation.errors.ownerName}</FormFeedback>
                        )}
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6} className="mb-3">
                        <Label className="form-label fw-semibold">
                          Mobile Number (Login ID) <span className="text-danger">*</span>
                        </Label>
                        <div className="input-group">
                          <span className="input-group-text bg-light">+91</span>
                          <Input
                            name="mobileNo"
                            maxLength="10"
                            placeholder="10-digit Mobile No"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.mobileNo}
                            invalid={validation.touched.mobileNo && !!validation.errors.mobileNo}
                            className="form-control-lg fs-6"
                          />
                        </div>
                        {validation.touched.mobileNo && validation.errors.mobileNo && (
                          <div className="text-danger small mt-1">{validation.errors.mobileNo}</div>
                        )}
                      </Col>

                      <Col md={6} className="mb-3">
                        <Label className="form-label fw-semibold">
                          Email Address <span className="text-muted">(Optional)</span>
                        </Label>
                        <Input
                          name="email"
                          type="email"
                          placeholder="firm@example.com"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.email}
                          invalid={validation.touched.email && !!validation.errors.email}
                          className="form-control-lg fs-6"
                        />
                        {validation.touched.email && validation.errors.email && (
                          <FormFeedback>{validation.errors.email}</FormFeedback>
                        )}
                      </Col>
                    </Row>

                    <Row>
                      <Col md={4} className="mb-3">
                        <Label className="form-label fw-semibold">
                          City <span className="text-danger">*</span>
                        </Label>
                        <Input
                          name="city"
                          placeholder="e.g. Indore"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.city}
                          invalid={validation.touched.city && !!validation.errors.city}
                          className="form-control-lg fs-6"
                        />
                        {validation.touched.city && validation.errors.city && (
                          <FormFeedback>{validation.errors.city}</FormFeedback>
                        )}
                      </Col>

                      <Col md={4} className="mb-3">
                        <Label className="form-label fw-semibold">
                          State <span className="text-danger">*</span>
                        </Label>
                        <Input
                          name="state"
                          placeholder="e.g. Madhya Pradesh"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.state}
                          invalid={validation.touched.state && !!validation.errors.state}
                          className="form-control-lg fs-6"
                        />
                        {validation.touched.state && validation.errors.state && (
                          <FormFeedback>{validation.errors.state}</FormFeedback>
                        )}
                      </Col>

                      <Col md={4} className="mb-3">
                        <Label className="form-label fw-semibold">
                          GSTIN <span className="text-muted">(Optional)</span>
                        </Label>
                        <Input
                          name="gstNo"
                          placeholder="22AAAAA0000A1Z5"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.gstNo}
                          className="form-control-lg fs-6 text-uppercase"
                        />
                      </Col>
                    </Row>

                    <h5 className="text-primary fw-semibold mt-3 mb-3 border-bottom pb-2">
                      <i className="fas fa-lock me-2"></i>Security & Password
                    </h5>

                    <Row>
                      <Col md={6} className="mb-3">
                        <Label className="form-label fw-semibold">
                          Password <span className="text-danger">*</span>
                        </Label>
                        <div className="position-relative">
                          <Input
                            name="password"
                            type={showPass ? "text" : "password"}
                            placeholder="Choose a strong password"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.password}
                            invalid={validation.touched.password && !!validation.errors.password}
                            className="form-control-lg fs-6"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPass(!showPass)}
                            className="btn btn-link position-absolute end-0 top-50 translate-middle-y text-muted"
                            style={{ textDecoration: "none" }}
                          >
                            <i className={showPass ? "fas fa-eye-slash" : "fas fa-eye"}></i>
                          </button>
                        </div>
                        {validation.touched.password && validation.errors.password && (
                          <FormFeedback>{validation.errors.password}</FormFeedback>
                        )}
                      </Col>

                      <Col md={6} className="mb-3">
                        <Label className="form-label fw-semibold">
                          Confirm Password <span className="text-danger">*</span>
                        </Label>
                        <Input
                          name="confirmPassword"
                          type={showPass ? "text" : "password"}
                          placeholder="Confirm your password"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.confirmPassword}
                          invalid={validation.touched.confirmPassword && !!validation.errors.confirmPassword}
                          className="form-control-lg fs-6"
                        />
                        {validation.touched.confirmPassword && validation.errors.confirmPassword && (
                          <FormFeedback>{validation.errors.confirmPassword}</FormFeedback>
                        )}
                      </Col>
                    </Row>

                    <div className="d-grid mt-4">
                      <Button
                        type="submit"
                        size="lg"
                        style={{
                          background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                          border: "none",
                          borderRadius: "12px",
                          padding: "14px",
                          fontSize: "17px",
                          fontWeight: "600",
                          boxShadow: "0 4px 15px rgba(56, 239, 125, 0.4)",
                        }}
                      >
                        <i className="fas fa-rocket me-2"></i>
                        Activate Firm & Start 7-Day Free Trial
                      </Button>
                    </div>

                    <div className="mt-4 text-center">
                      <p className="text-muted mb-0">
                        Pehle se registered hain?{" "}
                        <Link to="/login" className="text-primary fw-bold">
                          Login Karein
                        </Link>
                      </p>
                    </div>
                  </Form>
                </CardBody>
              </Card>

              {/* Support Hotline */}
              <div className="text-center mt-4">
                <p className="text-white-50 mb-0">
                  Kisi bhi sahayata ke liye call karein:{" "}
                  <a href="tel:+919999999999" className="text-white fw-bold">
                    +91 99999-99999
                  </a>
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Register;
